import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { LogOut, Menu, X } from "lucide-react";
import ConfirmLogoutButton from "@/components/ConfirmLogoutButton";
import { LOGO_URL } from "@/components/site/SiteHeader";

const NavItem = ({ item, active, testId, onClick }) => (
  <Link
    to={item.path}
    onClick={onClick}
    data-testid={testId}
    className={`flex items-center gap-3 px-4 py-3 rounded-2xl font-semibold transition-colors ${active ? "bg-secondary-500 text-primary-900 shadow-[0_3px_0_#B45309]" : "text-primary-100 hover:bg-white/10 hover:text-white"}`}
  >
    <item.icon className="w-5 h-5" />
    <span>{item.label}</span>
  </Link>
);

const MobileTabs = ({ items, pathname, onMenu, testIdFor }) => (
  <nav className="lg:hidden fixed bottom-0 inset-x-0 z-50 bg-white/95 backdrop-blur-xl border-t border-primary-100 shadow-[0_-8px_30px_rgba(76,29,149,0.12)] pb-[env(safe-area-inset-bottom)]" data-testid="panel-mobile-tabs">
    <div className="grid grid-cols-5 h-16">
      {items.slice(0, 4).map((it) => {
        const active = pathname === it.path;
        return (
          <Link key={it.path} to={it.path} className="flex flex-col items-center justify-center gap-0.5" data-testid={`tab-${testIdFor(it)}`}>
            <span className={`px-4 py-1 rounded-full transition-colors ${active ? "bg-primary-100 text-primary-700" : "text-primary-400"}`}><it.icon className="w-5 h-5" /></span>
            <span className={`text-[11px] font-bold truncate max-w-[72px] ${active ? "text-primary-700" : "text-primary-400"}`}>{it.short || it.label}</span>
          </Link>
        );
      })}
      <button type="button" onClick={onMenu} className="flex flex-col items-center justify-center gap-0.5 text-primary-400" data-testid="panel-tab-more">
        <span className="px-4 py-1"><Menu className="w-5 h-5" /></span>
        <span className="text-[11px] font-bold">More</span>
      </button>
    </div>
  </nav>
);

export const PanelShell = ({ badge, menuItems, mobileTabs, testIdFor, logoutRedirect, logoutTestId, headerRight, sidebarExtra, title, children }) => {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  return (
    <div className="min-h-screen bg-[#FAF7FF]">
      <header className="fixed top-0 inset-x-0 h-16 bg-primary-700 z-50 shadow-lg shadow-primary-900/20">
        <div className="flex items-center justify-between h-full px-3 sm:px-4 lg:px-6 gap-3">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <button onClick={() => setOpen(!open)} className="lg:hidden w-10 h-10 rounded-full flex items-center justify-center text-white hover:bg-white/10" data-testid="mobile-sidebar-toggle" aria-label="Menu">
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            <Link to="/" className="shrink-0 bg-white rounded-2xl px-2.5 py-1 -rotate-2"><img src={LOGO_URL} alt="M Ventures" className="h-7 w-auto" /></Link>
            <span className="inline-flex text-[10px] sm:text-[11px] font-extrabold uppercase tracking-wider bg-secondary-500 text-primary-900 rounded-full px-2 sm:px-3 py-1 whitespace-nowrap" data-testid="panel-badge">{badge}</span>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">{headerRight}</div>
        </div>
      </header>

      <aside className={`fixed top-16 left-0 bottom-0 w-72 lg:w-64 bg-primary-900 transform transition-transform duration-200 ease-out z-40 flex flex-col ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`} data-testid="panel-sidebar">
        <nav className="p-4 space-y-1 overflow-y-auto flex-1">
          {menuItems.map((item) => (
            <NavItem key={item.path} item={item} active={pathname === item.path} testId={testIdFor(item)} onClick={() => setOpen(false)} />
          ))}
        </nav>
        <div className="p-4 border-t border-white/10 space-y-1 pb-24 lg:pb-4">
          {sidebarExtra}
          <ConfirmLogoutButton redirectTo={logoutRedirect}>
            <button className="flex items-center gap-3 px-4 py-3 w-full rounded-2xl text-pink-300 hover:bg-pink-500/10 font-semibold transition-colors" data-testid={logoutTestId}>
              <LogOut className="w-5 h-5" />Logout
            </button>
          </ConfirmLogoutButton>
        </div>
      </aside>
      {open && <div className="fixed inset-0 bg-primary-900/40 backdrop-blur-sm z-30 lg:hidden" onClick={() => setOpen(false)} />}

      <main className="lg:ml-64 pt-16 min-h-screen pb-24 lg:pb-0">
        <div className="p-4 sm:p-6 lg:p-8">
          {title && <h1 className="font-heading text-2xl md:text-3xl font-extrabold text-primary-900 tracking-tight mb-6" data-testid="panel-title">{title}</h1>}
          {children}
        </div>
      </main>
      <MobileTabs items={mobileTabs || menuItems} pathname={pathname} onMenu={() => setOpen(true)} testIdFor={testIdFor} />
    </div>
  );
};
